import type { Key } from './en';

const ru: Record<Key, string> = {
  'app.title': 'Стереограммы',
  'nav.studio': 'Создать',
  'nav.animate': 'Анимация',
  'nav.gallery': 'Галерея',
  'mode.full': 'дом · GPU',
  'mode.clientOnly': 'в браузере',
  'mode.hint': 'Где выполняются вычисления',

  'common.add': 'Добавить',
  'common.remove': 'Удалить',
  'common.reset': 'Сброс',
  'common.off': 'выкл',
  'common.cancel': 'Отмена',

  'time.seconds': '{n} с',
  'time.minutes': '{n} мин',
  'time.minSec': '{m} мин {s} с',
  'eta.about': '~{t}',

  'error.decode': 'Не удалось прочитать файл: {msg}',
  'error.font': 'Не удалось загрузить шрифт: {msg}',

  'source.title': 'Источник',
  'source.text': '3D-текст',
  'source.image': 'Картинка',
  'source.depth': 'Карта глубины',
  'source.dropImage': 'Перетащите изображение или нажмите',
  'source.dropDepth': 'Перетащите карту глубины (лучше PNG16) или нажмите',
  'source.depthHint': 'Белое — ближе, чёрное — дальше. 16-битный PNG в оттенках серого сохраняет полную точность.',
  'source.method': 'Оценка глубины',

  'heuristic.brightness': 'Яркость (светлее — ближе)',
  'heuristic.edges': 'Контуры (замкнутые формы выпуклые)',
  'heuristic.threshold': 'Порог контуров',

  'text.content': 'Текст',
  'text.font': 'Шрифт',
  'text.uploadFont': 'Загрузить TTF/OTF',
  'text.fill': 'Размер в кадре',
  'text.extrude': 'Толщина',
  'text.bevel': 'Фаска',
  'text.base': 'Глубина тыла',
  'text.top': 'Глубина лица',
  'text.rotX': 'Наклон X',
  'text.rotY': 'Поворот Y',
  'text.rotZ': 'Крен Z',

  'output.title': 'Результат',
  'output.width': 'Ширина',
  'output.height': 'Высота',
  'output.maxSide': 'Длинная сторона',
  'output.result': 'Итог: {w}×{h}',

  'depth.title': 'Обработка глубины',
  'depth.normalize': 'Нормализовать',
  'depth.invert': 'Инвертировать',
  'depth.clip': 'Отсечь выбросы',
  'depth.far': 'Дальняя граница',
  'depth.near': 'Ближняя граница',
  'depth.gamma': 'Гамма',
  'depth.smoothing': 'Сглаживание с краями',
  'depth.smooth.none': 'Нет',
  'depth.smooth.bilateral': 'Билатеральное',
  'depth.smooth.guided': 'Guided',
  'depth.radius': 'Радиус',
  'depth.blur': 'Размытие',
  'depth.levels': 'Слои',
  'depth.rangeMin': 'Мин. на выходе',
  'depth.rangeMax': 'Макс. на выходе',

  'texture.title': 'Текстура',
  'texture.kind': 'Узор',
  'texture.kind.random_dots': 'Случайные точки (SIRDS)',
  'texture.kind.noise': 'Шум Перлина',
  'texture.kind.worley': 'Ячейки (Уорли)',
  'texture.kind.stripes': 'Полосы',
  'texture.kind.marble': 'Мрамор',
  'texture.kind.camo': 'Камуфляж',
  'texture.kind.image': 'Своё изображение',
  'texture.kind.source': 'Цвета из исходника',
  'texture.style.bw': 'Ч/Б',
  'texture.style.color': 'Цвет',
  'texture.style.palette': 'Палитра',
  'texture.density': 'Плотность',
  'texture.dotSize': 'Размер точки',
  'texture.palette': 'Палитра',
  'texture.color': 'Цвет',
  'texture.scale': 'Масштаб',
  'texture.octaves': 'Детализация (октавы)',
  'texture.stripeWidth': 'Ширина полос',
  'texture.waviness': 'Волнистость',
  'texture.turbulence': 'Турбулентность',
  'texture.upload': 'Перетащите изображение текстуры или нажмите',
  'texture.needImage': 'Загрузите изображение текстуры.',
  'texture.seamless': 'Бесшовность',
  'texture.seamless.blend': 'Смешать края',
  'texture.seamless.mirror': 'Отразить',
  'texture.seamless.none': 'Как есть',
  'texture.sourceHint':
    'Серый узор из точек несёт глубину и окрашивается цветами исходника — фото проступает сквозь 3D-изображение.',

  'stereo.title': 'Стереограмма',
  'stereo.wallEyed': 'Параллельно',
  'stereo.crossEyed': 'Перекрёстно',
  'stereo.patternWidth': 'Ширина узора',
  'stereo.patternWidthHint': 'Период повтора фона. «Авто» вычисляет его из расстояния между глазами и DPI.',
  'stereo.auto': 'авто · {px} px',
  'stereo.eyeSep': 'Между глазами, мм',
  'stereo.dpi': 'DPI экрана',
  'stereo.mu': 'Глубина сцены (μ)',
  'stereo.muHint': 'Насколько сцена выступает к зрителю. Больше — глубже, но труднее совместить.',
  'stereo.oversample': 'Сверхсэмплинг',
  'stereo.oversampleHint': 'Горизонтальный сверхсэмплинг: плавнее ступени глубины, но медленнее.',
  'stereo.seed': 'Seed',
  'stereo.randomSeed': 'Случайный seed',
  'stereo.guideDots': 'Точки-ориентиры',

  'preview.stereogram': 'Стереограмма',
  'preview.depth': 'Глубина',
  'preview.texture': 'Текстура',
  'preview.fit': 'Вписать',
  'preview.rendering': 'Рендер…',
  'preview.empty': 'Выберите источник слева',
  'preview.downloadPng': 'Скачать PNG',
  'preview.downloadDepth': 'Глубина PNG16',
  'preview.depth16Hint': 'Обработанная карта глубины как 16-битный PNG',

  'anim.source': 'Источник анимации',
  'anim.procedural': 'Процедурная',
  'anim.sequence': 'Последовательность',
  'anim.preset': 'Движение',
  'anim.preset.turn': 'Полный оборот (Y)',
  'anim.preset.swing': 'Покачивание (Y)',
  'anim.preset.tilt': 'Кивок (X)',
  'anim.preset.spin': 'Вращение (Z)',
  'anim.preset.jump': 'Прыжок',
  'anim.preset.pulse': 'Пульсация',
  'anim.preset.wave': 'Волна',
  'anim.amount': 'Сила',
  'anim.cycles': 'Повторов за цикл',
  'anim.proc.waves': 'Волны',
  'anim.proc.noise': 'Шум',
  'anim.proc.ripple': 'Рябь',
  'anim.amplitude': 'Амплитуда',
  'anim.wavelength': 'Длина волны',
  'anim.direction': 'Направление',
  'anim.decay': 'Затухание',
  'anim.centerX': 'Центр X',
  'anim.centerY': 'Центр Y',
  'anim.base': 'Поверх',
  'anim.base.flat': 'Плоского фона',
  'anim.base.studio': 'Статики из «Создать»',
  'anim.dropZip': 'Перетащите ZIP с кадрами глубины PNG16 или нажмите',
  'anim.noPngInZip': 'В архиве нет PNG-файлов',
  'anim.mixedSizes': 'Все кадры должны быть одного размера',
  'anim.filter': 'Временное сглаживание',
  'anim.filter.none': 'Нет',
  'anim.filter.ema': 'Экспоненциальное (EMA)',
  'anim.filter.median': 'Медиана по окну',
  'anim.emaAlpha': 'Отзывчивость',
  'anim.medianRadius': 'Радиус окна',
  'anim.threshold': 'Порог движения',
  'anim.thresholdHint': 'Изменения больше порога не сглаживаются, чтобы быстрое движение не размывалось.',
  'anim.timing': 'Время и размер',
  'anim.fps': 'Кадров/с',
  'anim.duration': 'Длительность',
  'anim.loop': 'По кругу',
  'anim.pingPong': 'Туда-обратно',
  'anim.plan': '{w}×{h} · {n} кадров · {fps} к/с',
  'anim.limits': 'Ограничения в браузере: ≤ {side} px, ≤ {s} с.',
  'anim.patternMotion': 'Узор между кадрами',
  'anim.motion.static': 'Статичный',
  'anim.motion.boiling': 'Кипящий',
  'anim.motion.scroll': 'Прокрутка',
  'anim.motionHint.static':
    'Один и тот же узор во всех кадрах, движется только скрытая фигура. Комфортнее всего для глаз.',
  'anim.motionHint.boiling':
    'Новые точки в каждом кадре: в отдельном кадре фигуры не видно, она проявляется только в движении.',
  'anim.motionHint.scroll': 'Узор плавно смещается по вертикали.',
  'anim.scrollSpeed': 'Скорость за кадр',
  'anim.generate': 'Создать анимацию',
  'anim.stage.depth': 'Глубина',
  'anim.stage.smooth': 'Сглаживание',
  'anim.stage.render': 'Кадры',
  'anim.left': 'осталось {t}',
  'anim.done': '{n} кадров за {t} · {mb} МБ в памяти',
  'anim.exporting': 'Экспорт {what}… {p}%',
  'anim.noCodec': 'Браузер не умеет кодировать этот формат',
  'anim.depthZip': 'Глубина ZIP',
  'anim.depthZipHint': 'Кадры глубины как 16-битные PNG в ZIP',
  'anim.empty': 'Настройте анимацию слева и нажмите «Создать анимацию»',
  'player.play': 'Воспроизвести',
  'player.pause': 'Пауза',
  'player.prev': 'Предыдущий кадр',
  'player.next': 'Следующий кадр',

  'heuristic.ml': 'Нейросеть (Depth Anything V2)',
  'source.mlHint': 'Яркость и контуры — быстрые эвристики; нейросеть даёт гораздо лучшую глубину.',
  'ml.info': 'Работает в браузере; модель (~30–50 МБ) скачивается один раз и кэшируется.',
  'ml.backend': 'бэкенд: {backend}',
  'ml.downloading': 'Загрузка модели {done}/{total} МБ…',
  'ml.init': 'Запуск модели…',
  'ml.inferring': 'Оценка глубины ({backend})',
  'brush.title': 'Кисть',
  'brush.hint': 'Доводка карты глубины рисованием',
  'brush.raise': 'Поднять',
  'brush.lower': 'Опустить',
  'brush.smooth': 'Сгладить',
  'brush.flatten': 'Выровнять',
  'brush.radius': 'Размер кисти',
  'brush.strength': 'Сила',
  'brush.undo': 'Отменить мазок',
  'brush.clear': 'Сбросить доводку',

  'anim.video': 'Видео',
  'anim.dropVideo': 'Перетащите видео или нажмите',
  'anim.videoHint':
    'Глубина оценивается покадрово в браузере (Depth Anything V2 Small), затем сглаживается по времени.',
  'anim.videoTrimmed': 'В браузере будут использованы только первые {s} с.',
  'anim.videoError': 'Не удалось открыть видео: {msg}',

  'compute.client': 'Браузер',
  'compute.server': 'Сервер',
  'compute.auto': 'Авто',
  'server.model': 'Модель (домашний сервер)',
  'server.mlInfo': 'Выполняется на GPU домашнего сервера',
  'server.slowHint': 'Это займёт время: попробуйте модель полегче или режим «в браузере».',
  'server.queued': 'В очереди: №{n}',
  'server.running': 'На сервере: {p}%',
  'server.format': 'Формат результата',
  'server.result': 'Результат с сервера',
  'server.download': 'Скачать',

  'anim.serverLimits': 'Ограничения домашнего сервера: ≤ {side} px, ≤ {s} с.',
  'anim.serverDone': '{n} кадров отрендерено на сервере за {t}',
  'server.zipReady': 'ZIP с PNG-кадрами готов к скачиванию.',

  'anim.slowClientHint':
    'Для браузера это долго: уменьшите размер, длительность или сверхсэмплинг либо используйте домашний сервер.',

  'nav.scene': 'Сцена',
  'source.scene': 'Сцена',
  'source.sceneHint': 'Глубина 3D-сцены из редактора ({n} объектов, t = {t} с).',
  'source.openEditor': 'Открыть редактор сцен',
  'anim.scene': 'Сцена',
  'anim.sceneHint': 'Таймлайн сцены из редактора ({d} с) рендерится покадрово.',
  'scene.add': 'Добавить',
  'scene.import': 'Импорт glTF / GLB / STL / OBJ',
  'scene.importError': 'Не удалось импортировать: {msg}',
  'scene.loadError': 'Не удалось открыть сцену: {msg}',
  'scene.outliner': 'Объекты',
  'scene.camera': 'Камера',
  'scene.visible': 'Видимость',
  'scene.emptyHint': 'Сцена пуста: добавьте объект.',
  'scene.group': 'Сгруппировать',
  'scene.properties': 'Свойства',
  'scene.selectHint': 'Выберите объект или камеру.',
  'scene.depthPreview': 'Глубина (камера сцены)',
  'scene.name': 'Имя',
  'scene.parent': 'Родитель',
  'scene.position': 'Позиция',
  'scene.rotation': 'Поворот°',
  'scene.scale': 'Масштаб',
  'scene.target': 'Смотрит на',
  'scene.duplicate': 'Дублировать',
  'scene.baking': 'Строю текст…',
  'scene.perspective': 'Перспектива',
  'scene.orthographic': 'Ортографическая',
  'scene.fov': 'Угол обзора°',
  'scene.viewHeight': 'Высота кадра',
  'scene.near': 'Ближняя (глубина 1)',
  'scene.far': 'Дальняя (глубина 0)',
  'scene.fitDepth': 'Подогнать глубину',
  'scene.fitDepthHint': 'Устанавливает near/far по содержимому за всю анимацию',
  'scene.depthRangeHint':
    'Расстояния между near и far становятся глубиной стереограммы: держите диапазон плотно вокруг объектов.',
  'scene.gizmo.translate': 'Двигать',
  'scene.gizmo.rotate': 'Вращать',
  'scene.gizmo.scale': 'Масштаб',
  'scene.throughCamera': 'Вид камеры',
  'scene.focus': 'Показать всё',
  'scene.new': 'Новая',
  'scene.open': 'Открыть…',
  'scene.save': 'Сохранить',
  'scene.toStudio': 'Стереограмма',
  'scene.toAnimate': 'Анимация',
  'scene.viewportHint':
    'Тянуть: вращение · правой: сдвиг · колесо: масштаб · W/E/R: двигать/вращать/масштаб · F: показать всё · Ctrl+Z: отмена',
  'scene.heightFromImage': 'Или загрузить из картинки:',
  'scene.presets': 'Пресеты…',
  'scene.preset.spin': 'Вращение',
  'scene.preset.jump': 'Прыжок',
  'scene.preset.pulse': 'Пульсация',
  'scene.preset.wave': 'Волна',
  'scene.preset.flythrough': 'Пролёт камеры',
  'scene.addKey': 'Ключ',
  'scene.noKeys': 'Нет ключей: перемещение объекта меняет его исходную позу.',
  'scene.selectForKeys': 'Выберите объект или камеру, чтобы редактировать ключи.',
  'scene.k.position': 'позиция',
  'scene.k.rotation': 'поворот',
  'scene.k.scale': 'масштаб',
  'scene.k.target': 'цель',
  'scene.ease.linear': 'Линейно',
  'scene.ease.ease_in': 'Разгон',
  'scene.ease.ease_out': 'Торможение',
  'scene.ease.ease_in_out': 'Плавно',
  'scene.ease.step': 'Скачком',
  'scene.shape.box': 'Куб',
  'scene.shape.sphere': 'Сфера',
  'scene.shape.torus': 'Тор',
  'scene.shape.cylinder': 'Цилиндр',
  'scene.shape.plane': 'Плоскость',
  'scene.shape.terrain': 'Рельеф',
  'scene.shape.heightmap': 'Карта высот',
  'scene.shape.group': 'Группа',
  'scene.shape.mesh': 'Меш',
  'scene.f.size': 'Размер',
  'scene.f.radius': 'Радиус',
  'scene.f.tube': 'Толщина',
  'scene.f.segments': 'Сегменты',
  'scene.f.rings': 'Кольца',
  'scene.f.radiusTop': 'Радиус сверху',
  'scene.f.radiusBottom': 'Радиус снизу',
  'scene.f.height': 'Высота',
  'scene.f.width': 'Ширина',
  'scene.f.relief': 'Рельеф',
  'scene.f.resolution': 'Разрешение',

  'common.close': 'Закрыть',
  'publish.button': 'Опубликовать',
  'publish.title': 'Публикация в галерее',
  'publish.name': 'Название (необязательно)',
  'publish.caption': 'Подпись (необязательно)',
  'publish.withParams': 'Поделиться настройками (глубина, текстура, стереограмма). Исходное фото не загружается.',
  'publish.moderation': 'Работы появляются в галерее после модерации.',
  'publish.send': 'Отправить',
  'publish.sending': 'Отправка…',
  'publish.done': 'Спасибо! Работа появится в галерее после модерации.',
  'gallery.kind.all': 'Все',
  'gallery.kind.static': 'Статичные',
  'gallery.kind.animation': 'Анимации',
  'gallery.count': 'Работ: {n}',
  'gallery.empty': 'Здесь пока ничего нет.',
  'gallery.untitled': 'Без названия',
  'gallery.useSettings': 'Использовать настройки',
  'admin.title': 'Модерация галереи',
  'admin.toSite': '← на сайт',
  'admin.logout': 'Выйти',
  'admin.login': 'Вход для администратора',
  'admin.password': 'Пароль',
  'admin.signIn': 'Войти',
  'admin.notConfigured': 'Пароль администратора не настроен на сервере (STEREO_ADMIN_PASSWORD_HASH).',
  'admin.stat.total': 'всего',
  'admin.stat.pending': 'на модерации',
  'admin.stat.approved': 'опубликовано',
  'admin.galleryEnabled': 'Галерея включена',
  'admin.galleryDisabledByConfig': 'Выключена в конфигурации сервера',
  'admin.status.pending': 'На модерации',
  'admin.status.approved': 'Опубликованные',
  'admin.status.hidden': 'Скрытые',
  'admin.status.rejected': 'Отклонённые',
  'admin.status.all': 'Все',
  'admin.selected': 'Выбрано: {n}',
  'admin.select': 'Выбрать',
  'admin.approve': 'Одобрить',
  'admin.hide': 'Скрыть',
  'admin.reject': 'Отклонить',
  'admin.empty': 'Нет работ.',
  'admin.hasParams': 'с настройками',
  'admin.confirmDelete': 'Удалить работу навсегда?',
  'admin.confirmDeleteMany': 'Удалить навсегда {n} работ?',

  'segment.title': 'Выделить объект',
  'segment.hint': 'Кликните по объекту на карте глубины, чтобы выделить его (SlimSAM, работает в браузере)',
  'segment.clickHint': 'Кликните по объекту; Shift/правый клик исключает область',
  'segment.working': 'Выделяю…',
  'segment.points': 'Точек: {n}',
  'segment.flat': 'Плоскость',
  'segment.tilt': 'Наклон',
  'segment.tiltHint': 'Превращает объект в наклонную плоскость',
  'segment.amount': 'Сила',
  'segment.angle': 'Направление наклона',
  'segment.clear': 'Снять выделение',

  'anim.morph': 'Морфинг',
  'anim.morphHint': 'Плавно превращает карту глубины A в B. Воспроизведение «туда-обратно» даёт бесшовный цикл.',
  'anim.morphStudio': 'статичный источник из «Создать»',
  'anim.morphA': 'Карта глубины A (необязательно)',
  'anim.morphB': 'Карта глубины B',

  'texture.tint': 'Сила цвета',

  'texture.presets': 'Готовые текстуры',
  'texture.palettePreset': 'Палитры…',
  'texture.kind.mosaic': 'Мозаика / витраж',
  'texture.kind.bubbles': 'Пузыри',
  'texture.kind.plasma': 'Плазма (психоделика)',
  'texture.kind.rings': 'Кольца / дерево',
  'texture.kind.confetti': 'Конфетти',
  'texture.kind.stars': 'Звёздное небо',
  'texture.edge': 'Швы',
  'texture.radius': 'Размер',
  'texture.bands': 'Цветовые полосы',
  'texture.rings': 'Кольца',
  'texture.pieceSize': 'Размер кусочков',
  'texture.preset.classic': 'Классика',
  'texture.preset.colorDots': 'Цветные точки',
  'texture.preset.neonDots': 'Неон',
  'texture.preset.plasma': 'Плазма',
  'texture.preset.psychedelic': 'Психоделика',
  'texture.preset.stainedGlass': 'Витраж',
  'texture.preset.cobblestone': 'Брусчатка',
  'texture.preset.bubbles': 'Пузыри',
  'texture.preset.pebbles': 'Галька',
  'texture.preset.confetti': 'Конфетти',
  'texture.preset.autumn': 'Осень',
  'texture.preset.wood': 'Дерево',
  'texture.preset.agate': 'Агат',
  'texture.preset.stars': 'Звёзды',
  'texture.preset.lava': 'Лава',
  'texture.preset.marble': 'Мрамор',
  'texture.preset.ocean': 'Океан',
  'texture.preset.jungle': 'Джунгли',
  'texture.preset.gold': 'Золото',
  'texture.preset.rainbowStripes': 'Радуга',
  'palette.ocean': 'Океан',
  'palette.sunset': 'Закат',
  'palette.forest': 'Лес',
  'palette.candy': 'Конфеты',
  'palette.fire': 'Огонь',
  'palette.neon': 'Неон',
  'palette.pastel': 'Пастель',
  'palette.earth': 'Земля',
  'palette.gold': 'Золото',
  'palette.ice': 'Лёд',
  'palette.mono': 'Чёрно-белая',
  'palette.rainbow': 'Радуга',

  'scene.exampleCastle': 'Пример: замок',
};

export default ru;
